import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  EmblaOptionsType,
  EmblaCarouselType,
  CreatePluginType,
  CreateOptionsType
} from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Fade from 'embla-carousel-fade'
import ClassNames from 'embla-carousel-class-names'
import AutoHeight from 'embla-carousel-auto-height'
import { ReactComponent as SliderArrow } from '@svgs/keyline/arrow-previous.svg'
import carouselStyles from './styles.module.scss'
import { debounce, objectKeys } from '@helpers/utils'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

export type { EmblaCarouselType as SliderCarouselType }

const preventEdgeScrolling = (embla: EmblaCarouselType) => {
  const { limit, target, location, scrollTo } = embla.internalEngine()

  return () => {
    if (limit.reachedMax(target.get())) {
      if (limit.reachedMax(location.get())) location.set(limit.max)
      target.set(limit.max)
      scrollTo.distance(0, false)
    }
    if (limit.reachedMin(target.get())) {
      if (limit.reachedMin(location.get())) location.set(limit.min)
      target.set(limit.min)
      scrollTo.distance(0, false)
    }
  }
}

type ButtonProps = {
  enabled: boolean
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}
const PrevButton = ({ enabled, onClick }: ButtonProps) => (
  <button
    type="button"
    className={`${carouselStyles.carouselButton} ${carouselStyles.carouselButton_prev}`}
    onClick={onClick}
    disabled={!enabled}
  >
    <SliderArrow />
  </button>
)

const NextButton = ({ enabled, onClick }: ButtonProps) => (
  <button
    type="button"
    className={`${carouselStyles.carouselButton} ${carouselStyles.carouselButton_next}`}
    onClick={onClick}
    disabled={!enabled}
  >
    <SliderArrow />
  </button>
)

export type ScrollCarouselProps = {
  children: React.ReactNode
  classes?: string
  settings?: EmblaOptionsType & {
    afterChange?: (slide: number) => void
    buttons?: {
      visible: {
        mobile?: boolean
        desktop?: boolean
      }
      position: string
    }
    fade?: boolean
    overflow?: boolean
    progressBar?: {
      visible: {
        mobile?: boolean
        desktop?: boolean
      }
    }
    plugins?: {
      fade?: boolean
      autoHeight?: boolean
    }
  }
  instance?: (embla: EmblaCarouselType) => void
  carouselClasses?: string
}

type CarouselPlugin = CreatePluginType<
  NonNullable<unknown>,
  CreateOptionsType<NonNullable<unknown>>
>

function getCarouselPlugins(setings: Partial<ScrollCarouselProps['settings']>) {
  const { plugins } = setings || {}
  const pluginsList: CarouselPlugin[] = []

  if (!plugins || objectKeys(plugins).length === 0) {
    return pluginsList
  }

  if (plugins?.fade) {
    pluginsList.push(Fade())
  }

  if (plugins?.autoHeight) {
    pluginsList.push(AutoHeight())
  }

  pluginsList.push(ClassNames())

  return pluginsList
}

function ScrollCarousel({
  children,
  classes,
  instance = () => undefined,
  settings = {},
  carouselClasses = ''
}: ScrollCarouselProps) {
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.DESKTOP,
    initialState: true
  })
  const [progressSteps, setProgressSteps] = useState<number[]>([])
  const plugins = useMemo(() => getCarouselPlugins(settings), [settings])
  const [emblaRef, embla] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
      inViewThreshold: 0.8,
      ...settings
    },
    plugins
  )

  const previousChildrenCountRef = useRef(React.Children.count(children))
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const getReachableSnaps = useCallback((embla: EmblaCarouselType) => {
    if (!embla) return []

    const reachableSnaps: number[] = []
    const startSnap = 0
    embla.scrollTo(startSnap, false) // go to first snap before calculating

    reachableSnaps.push(startSnap)

    while (embla.canScrollNext()) {
      embla.scrollNext()
      const currentSnap = embla.selectedScrollSnap()
      if (reachableSnaps.includes(currentSnap)) break
      reachableSnaps.push(currentSnap)
    }

    embla.scrollTo(startSnap, false) // return to start
    return reachableSnaps
  }, [])

  const scrollPrev = useCallback(() => {
    embla?.scrollPrev()
  }, [embla])

  const scrollNext = useCallback(() => {
    embla?.scrollNext()
  }, [embla])

  const onSelect = useCallback(() => {
    if (!embla) return

    setSelectedIndex(embla.selectedScrollSnap())
    setPrevBtnEnabled(embla.canScrollPrev())
    setNextBtnEnabled(embla.canScrollNext())

    if (settings.afterChange) {
      settings.afterChange(embla.selectedScrollSnap())
    }
  }, [embla, settings])

  useEffect(() => {
    if (!embla || !isReady) return

    const handleCarouselInit = () => {
      setProgressSteps(getReachableSnaps(embla))
      onSelect()
    }

    handleCarouselInit()

    const debouncedOnResize = debounce(() => {
      onSelect()
      setProgressSteps(getReachableSnaps(embla))
    }, 200) // adjust 200ms debounce delay as needed

    embla.on('select', onSelect).on('resize', debouncedOnResize)

    if (!settings.loop) {
      embla.on('scroll', preventEdgeScrolling(embla))
    }

    return () => {
      embla.off('select', onSelect)
      embla.off('resize', debouncedOnResize)
      if (!settings.loop) {
        embla.off('scroll', preventEdgeScrolling(embla))
      }
    }
  }, [embla, isReady])

  useEffect(() => {
    if (!embla) return

    setIsReady(true)
    instance(embla)
  }, [embla])

  useEffect(() => {
    if (!embla) return

    const newCount = React.Children.count(children)
    if (newCount !== previousChildrenCountRef.current) {
      previousChildrenCountRef.current = newCount
      embla.reInit()
      embla.scrollTo(settings.startIndex || 0)
    }
  }, [embla, children, settings.startIndex])

  const showButtons =
    settings.buttons?.visible &&
    ((isMobile && settings.buttons.visible.mobile) ||
      (!isMobile && settings.buttons.visible.desktop))

  const showProgressBar =
    settings.progressBar?.visible &&
    ((isMobile && settings.progressBar.visible.mobile) ||
      (!isMobile && settings.progressBar.visible.desktop))

  return (
    <div
      className={`${carouselStyles.ScrollCarousel} ${carouselClasses} ${
        settings.overflow ? carouselStyles.overflow : ''
      } ${classes ?? ''}`}
      ref={emblaRef}
    >
      {embla &&
        embla.slideNodes().length > 0 &&
        settings.buttons?.position === 'top' && (
          <div className={carouselStyles.ScrollCarousel__actionsContainer}>
            {showButtons && (
              <div className={carouselStyles.ButtonsContainer}>
                <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
                <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
              </div>
            )}
          </div>
        )}
      <div
        className={`${carouselStyles.ScrollCarousel__itemsContainer} ${
          settings?.plugins?.fade ? carouselStyles.fade : ''
        } ${isReady ? carouselStyles.isReady : ''}`}
      >
        {React.Children.map(children, (child, index) =>
          React.isValidElement(child)
            ? React.cloneElement(
                child as React.ReactElement<{ active: boolean }>,
                {
                  key: child.key ?? index,
                  active: index === selectedIndex
                }
              )
            : null
        )}
      </div>
      {embla && embla.slideNodes().length > 0 && showProgressBar && (
        <div className={carouselStyles.ScrollCarousel__actionsContainer}>
          {showProgressBar && (
            <div className={carouselStyles.carouselProgressBar}>
              <div
                style={{
                  left: `${(selectedIndex / progressSteps.length) * 100}%`,
                  width: `${100 / progressSteps.length}%`,
                  transition: 'left 0.3s ease'
                }}
                className={`${carouselStyles.carouselProgressBar__step} `}
              />
            </div>
          )}

          {showButtons && settings.buttons?.position === 'bottom' && (
            <div className={carouselStyles.ButtonsContainer}>
              <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
              <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ScrollCarousel
